import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  FiLogOut, FiUser, FiEdit, FiSave, FiX, FiPlus, 
  FiMessageSquare, FiBarChart2, FiSettings, FiCheckSquare, 
  FiArrowLeft, FiArrowRight, FiList
} from 'react-icons/fi';
import fundoImg from '../assets/fundo.png';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const dashboardAdminBackgroundStyle = {
  backgroundImage: `url(${fundoImg})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center'
};

const DashboardAdmin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Dados globais
  const [chapters, setChapters] = useState([]);
  const [essayAnswers, setEssayAnswers] = useState([]);
  const [tutoringTopics, setTutoringTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tutoringLoading, setTutoringLoading] = useState(false);
  
  // Navegação e UI
  const [activeLesson, setActiveLesson] = useState(null); 
  const [activeTab, setActiveTab] = useState('pending'); 
  const [sortOrder, setSortOrder] = useState('newest');
  const [tutoringStatusFilter, setTutoringStatusFilter] = useState('all');
  
  // Estados de Correção (Discursivas)
  const [editingId, setEditingId] = useState(null);
  const [grade, setGrade] = useState('');
  const [feedback, setFeedback] = useState('');
  
  // Estados de Tutoria
  const [tutoringAnsweringId, setTutoringAnsweringId] = useState(null);
  const [tutoringAnswerText, setTutoringAnswerText] = useState('');
  const [expandedTopicId, setExpandedTopicId] = useState(null);
  const [expandedTopicComments, setExpandedTopicComments] = useState([]);
  const [topChapters, setTopChapters] = useState([]);
  const [insightsPeriod, setInsightsPeriod] = useState('30');
  const [tutoringError, setTutoringError] = useState('');

  // Estados de Tutorias Gravadas
  const [recordedVideos, setRecordedVideos] = useState([]);
  const [recordedVideosLoading, setRecordedVideosLoading] = useState(false);
  const [recordedVideosError, setRecordedVideosError] = useState('');
  const [editingRecordedVideoId, setEditingRecordedVideoId] = useState(null);
  const [recordedVideoForm, setRecordedVideoForm] = useState({
    title: '',
    videoUrl: '',
    orderIndex: ''
  });

  const sameChapterId = (firstId, secondId) => (
    firstId !== null && firstId !== undefined &&
    secondId !== null && secondId !== undefined &&
    String(firstId) === String(secondId)
  );

  const isEssayPending = (answer) => Number(answer.is_graded) !== 1;
  const isTutoringPending = (topic) => !String(topic.answer_text || '').trim();

  const sortByDate = (items, getDate) => [...items].sort((first, second) => {
    const firstTime = new Date(getDate(first) || 0).getTime() || 0;
    const secondTime = new Date(getDate(second) || 0).getTime() || 0;
    return sortOrder === 'newest' ? secondTime - firstTime : firstTime - secondTime;
  });

  const fetchChapters = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tutoring/chapters`);
      const chaptersData = response.data.chapters || [];
      setChapters(chaptersData);
    } catch (error) {
      console.error('Erro ao buscar aulas:', error);
    }
  }, []);

  const fetchEssayAnswers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/essay-answers`);
      setEssayAnswers(response.data.answers || []);
    } catch (error) {
      console.error('Erro ao buscar respostas discursivas:', error);
    }
  }, []);

  const fetchTutoringTopics = useCallback(async () => {
    setTutoringLoading(true);
    setTutoringError('');
    try {
      const allTopics = [];
      let page = 1;
      let total = Infinity;

      while (allTopics.length < total) {
        const response = await axios.get(`${API_URL}/tutoring/topics?status=all&page=${page}&page_size=100`);
        const topics = response.data.topics || [];
        total = response.data.pagination?.total ?? topics.length;
        allTopics.push(...topics);

        if (topics.length === 0) break;
        page += 1;
      }

      setTutoringTopics(allTopics);
    } catch (error) {
      setTutoringError('Erro ao buscar tópicos de tutoria. Os humanos devem ter tropeçado no servidor.');
    } finally {
      setTutoringLoading(false);
    }
  }, []);

  const fetchTopChapters = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tutoring/insights/top-chapters?period=${insightsPeriod}`);
      setTopChapters(response.data.insights || []);
    } catch (error) {
      console.error('Erro ao buscar insights de tutoria:', error);
    }
  }, [insightsPeriod]);

  const fetchRecordedVideos = useCallback(async () => {
    setRecordedVideosLoading(true);
    setRecordedVideosError('');

    try {
      const params = new URLSearchParams();
      if (activeLesson) {
        params.append('chapter_id', activeLesson);
      }

      const query = params.toString();
      const response = await axios.get(`${API_URL}/tutoring/recorded-videos${query ? `?${query}` : ''}`);
      setRecordedVideos(response.data.recordedVideos || []);
    } catch (error) {
      setRecordedVideos([]);
      setRecordedVideosError('Erro ao buscar tutorias gravadas.');
    } finally {
      setRecordedVideosLoading(false);
    }
  }, [activeLesson]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchChapters(),
        fetchEssayAnswers(),
        fetchTutoringTopics()
      ]);
    } catch (error) {
      console.error('Falha nos sistemas de comunicação:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchChapters, fetchEssayAnswers, fetchTutoringTopics]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (activeTab === 'tutoring') {
      fetchTutoringTopics();
      fetchRecordedVideos();
      fetchTopChapters();
    }
  }, [activeTab, fetchRecordedVideos, fetchTutoringTopics, fetchTopChapters]);

  const lessonStatuses = useMemo(() => {
    const statuses = new Map(
      chapters.map((chapter) => [String(chapter.id), { pendingEssays: 0, pendingTutoring: 0 }])
    );

    essayAnswers.forEach((answer) => {
      const status = statuses.get(String(answer.chapter_id));
      if (status && isEssayPending(answer)) status.pendingEssays += 1;
    });

    tutoringTopics.forEach((topic) => {
      const status = statuses.get(String(topic.chapter_id));
      if (status && isTutoringPending(topic)) status.pendingTutoring += 1;
    });

    return statuses;
  }, [chapters, essayAnswers, tutoringTopics]);

  const getLessonStatusInfo = (chapterId) => {
    const status = lessonStatuses.get(String(chapterId)) || { pendingEssays: 0, pendingTutoring: 0 };
    const hasPendingEssays = status.pendingEssays > 0;
    const hasPendingTutoring = status.pendingTutoring > 0;

    if (hasPendingEssays) return { color: 'bg-red-500 hover:bg-red-600', text: 'Correção Pendente' };
    if (hasPendingTutoring) return { color: 'bg-yellow-500 hover:bg-yellow-600', text: 'Tutoria Pendente' };
    return { color: 'bg-green-500 hover:bg-green-600', text: 'Sem pendências' };
  };

  const activeLessonIndex = chapters.findIndex(c => sameChapterId(c.id, activeLesson));
  const hasPreviousLesson = activeLessonIndex > 0;
  const hasNextLesson = activeLessonIndex < chapters.length - 1 && activeLessonIndex !== -1;

  const goToLesson = (lessonId) => {
    setActiveLesson(String(lessonId));
    setActiveTab('pending');
  };

  const handleStartGrading = (answer) => {
    setEditingId(answer.attempt_id);
    setGrade(answer.grade || '');
    setFeedback(answer.feedback || '');
  };

  const handleCancelGrading = () => {
    setEditingId(null);
    setGrade('');
    setFeedback('');
  };

  const handleSubmitGrade = async (attemptId) => {
    if (!grade || grade < 0 || grade > 100) {
      alert('A nota deve estar entre 0 e 100. Leis da matemática, desculpe.');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/essay-answers/${attemptId}/grade`, { grade: parseInt(grade), feedback });
      setEditingId(null);
      setGrade('');
      setFeedback('');
      fetchEssayAnswers();
    } catch (error) {
      alert('Erro ao corrigir resposta: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleStartAnswer = (topic) => {
    setTutoringAnsweringId(topic.id);
    setTutoringAnswerText(topic.answer_text || '');
  };

  const handleCancelAnswer = () => {
    setTutoringAnsweringId(null);
    setTutoringAnswerText('');
  };

  const handleSubmitAnswer = async (topicId) => {
    if (!tutoringAnswerText.trim()) {
      alert('A resposta não pode estar vazia. O silêncio não é uma resposta válida aqui.');
      return;
    }
    try {
      await axios.put(`${API_URL}/tutoring/topics/${topicId}/answer`, { answer_text: tutoringAnswerText });
      setTutoringAnsweringId(null);
      setTutoringAnswerText('');
      fetchTutoringTopics();
    } catch (error) {
      alert('Erro ao responder tópico: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleFaq = async (topic) => {
    try {
      await axios.patch(`${API_URL}/tutoring/topics/${topic.id}/faq`, { is_faq: topic.is_faq !== 1 });
      fetchTutoringTopics();
    } catch (error) {
      alert('Erro ao atualizar FAQ: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleStartRecordedVideoEdit = (recordedVideo) => {
    setEditingRecordedVideoId(recordedVideo.id);
    setRecordedVideoForm({
      title: recordedVideo.title,
      videoUrl: recordedVideo.video_url,
      orderIndex: String(recordedVideo.order_index)
    });
  };

  const handleCancelRecordedVideoEdit = () => {
    setEditingRecordedVideoId(null);
    setRecordedVideoForm({
      title: '',
      videoUrl: '',
      orderIndex: ''
    });
  };

  const handleSubmitRecordedVideo = async () => {
    const chapterId = activeLesson;

    if (!chapterId || !recordedVideoForm.title.trim() || !recordedVideoForm.videoUrl.trim() || !recordedVideoForm.orderIndex) {
      alert('Preencha título, link e ordem');
      return;
    }

    try {
      const payload = {
        chapter_id: parseInt(chapterId, 10),
        title: recordedVideoForm.title,
        video_url: recordedVideoForm.videoUrl,
        order_index: parseInt(recordedVideoForm.orderIndex, 10)
      };

      if (editingRecordedVideoId) {
        await axios.put(`${API_URL}/tutoring/recorded-videos/${editingRecordedVideoId}`, payload);
      } else {
        await axios.post(`${API_URL}/tutoring/recorded-videos`, payload);
      }

      handleCancelRecordedVideoEdit();
      fetchRecordedVideos();
      alert('Tutoria gravada salva com sucesso!');
    } catch (error) {
      alert('Erro ao salvar tutoria gravada: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteRecordedVideo = async (recordedVideoId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta tutoria gravada?')) return;

    try {
      await axios.delete(`${API_URL}/tutoring/recorded-videos/${recordedVideoId}`);
      if (editingRecordedVideoId === recordedVideoId) {
        handleCancelRecordedVideoEdit();
      }
      fetchRecordedVideos();
      alert('Tutoria gravada deletada com sucesso!');
    } catch (error) {
      alert('Erro ao deletar tutoria gravada: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleDetails = async (topicId) => {
    if (expandedTopicId === topicId) {
      setExpandedTopicId(null);
      setExpandedTopicComments([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/tutoring/topics/${topicId}`);
      setExpandedTopicId(topicId);
      setExpandedTopicComments(response.data.comments || []);
    } catch (error) {
      alert('Erro ao buscar comentários: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRemoveComment = async (commentId) => {
    try {
      await axios.patch(`${API_URL}/tutoring/comments/${commentId}/remove`);
      if (expandedTopicId) {
        const response = await axios.get(`${API_URL}/tutoring/topics/${expandedTopicId}`);
        setExpandedTopicComments(response.data.comments || []);
      }
    } catch (error) {
      alert('Erro ao remover comentário: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pendingAnswers = essayAnswers.filter(isEssayPending);
  const gradedAnswers = essayAnswers.filter(answer => !isEssayPending(answer));
  
  const filteredPendingAnswers = pendingAnswers.filter(answer => sameChapterId(answer.chapter_id, activeLesson));
  const filteredGradedAnswers = gradedAnswers.filter(answer => sameChapterId(answer.chapter_id, activeLesson));
  const filteredTutoringTopics = tutoringTopics.filter((topic) => {
    if (!sameChapterId(topic.chapter_id, activeLesson)) return false;
    if (tutoringStatusFilter === 'unanswered') return isTutoringPending(topic);
    if (tutoringStatusFilter === 'answered') return !isTutoringPending(topic);
    return true;
  });

  const sortedPendingAnswers = sortByDate(filteredPendingAnswers, answer => answer.attempted_at);
  const sortedGradedAnswers = sortByDate(filteredGradedAnswers, answer => answer.graded_at || answer.attempted_at);
  const sortedTutoringTopics = sortByDate(filteredTutoringTopics, topic => topic.created_at);

  // Como recordedVideos já vem filtrado pela aula ativa, basta ordenar pela ordem definida.
  const sortedRecordedVideos = useMemo(() => (
    [...recordedVideos].sort((a, b) => a.order_index - b.order_index)
  ), [recordedVideos]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-xl font-bold animate-pulse">Carregando painel. Por favor, não entre em pânico...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-100" style={dashboardAdminBackgroundStyle}>
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 px-6 py-4 flex-shrink-0 shadow-sm z-30">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FiSettings className="text-indigo-600" /> Painel Admin
            </h1>
            <p className="text-sm text-gray-600 mt-1">Nível de Acesso: <span className="uppercase font-semibold">{user?.role}</span></p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FiUser className="text-gray-400" />
              <span>{user?.username}</span>
            </div>
            <button onClick={() => navigate('/profile')} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition">
              <FiUser />
              <span className="hidden sm:inline">Perfil</span>
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition">
              <FiLogOut />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto w-full px-4 sm:px-6 py-8">
        <div className="max-w-7xl mx-auto bg-white/95 backdrop-blur-sm shadow-2xl rounded-3xl p-6 sm:p-10 min-h-full border border-gray-100">
          
          {user?.role === 'admin_master' && (
            <div className="mb-8 flex gap-4 flex-wrap pb-6 border-b border-gray-200">
              <button onClick={() => navigate('/admin-master/create-chapter')} className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold">
                <FiPlus /> Criar Novo Tema
              </button>
              <button onClick={() => navigate('/admin-master/manage-content')} className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold">
                <FiEdit /> Gerenciar Temas e Capítulos
              </button>
              <button onClick={() => navigate('/admin-master/manage-users')} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-semibold">
                <FiUser /> Gerenciar Usuários Admin
              </button>
            </div>
          )}

          {!activeLesson ? (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <FiList className="text-2xl text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-800">Visão Geral das Aulas</h2>
              </div>
              
              <div className="flex gap-4 mb-6 text-sm flex-wrap">
                <span className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-red-500"></div> Ex. Pendente</span>
                <span className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-yellow-500"></div> Tutoria Pendente</span>
                <span className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-green-500"></div> Sem pendências</span>
              </div>

              {chapters.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-500">
                  Nenhuma aula cadastrada ou encontrada no banco de dados.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {chapters.map((chapter) => {
                    const status = getLessonStatusInfo(chapter.id);
                    return (
                      <button 
                        key={chapter.id} 
                        onClick={() => goToLesson(chapter.id)}
                        className={`${status.color} text-white p-6 rounded-xl text-left font-bold shadow-md transform hover:scale-[1.02] transition-all duration-200 flex flex-col justify-between min-h-[120px] cursor-pointer`}
                      >
                        <div>
                          <span className="text-xs uppercase tracking-wider opacity-80 block mb-1">
                            {chapter.category_name || 'Geral'}
                          </span>
                          <h3 className="text-lg leading-tight">{chapter.title || `Aula #${chapter.id}`}</h3>
                        </div>
                        <span className="text-xs font-semibold bg-black/20 self-start px-2.5 py-1 rounded mt-4">
                          {status.text}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex flex-col sm:flex-row items-center justify-between mb-8 bg-gray-50 p-4 rounded-xl border border-gray-200 gap-4">
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setActiveLesson(null)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-200 transition text-sm font-semibold whitespace-nowrap"
                  >
                    Voltar ao Painel
                  </button>
                  <select
                    aria-label="Escolher outra aula"
                    value={activeLesson}
                    onChange={(e) => goToLesson(e.target.value)}
                    className="w-full sm:w-80 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-700"
                  >
                    {chapters.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.category_name} - {c.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <button
                    type="button"
                    title="Aula anterior"
                    aria-label="Aula anterior"
                    onClick={() => goToLesson(chapters[activeLessonIndex - 1].id)}
                    disabled={!hasPreviousLesson}
                    className="p-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    <FiArrowLeft />
                  </button>
                  <button
                    type="button"
                    title="Próxima aula"
                    aria-label="Próxima aula"
                    onClick={() => goToLesson(chapters[activeLessonIndex + 1].id)}
                    disabled={!hasNextLesson}
                    className="p-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    <FiArrowRight />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <FiCheckSquare className="text-2xl text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-800">
                  Central de Correção: {chapters[activeLessonIndex]?.title}
                </h2>
              </div>

              <div className="mb-6 border-b border-gray-200">
                <div className="flex gap-6 overflow-x-auto pb-2">
                  <button 
                    onClick={() => setActiveTab('pending')}
                    className={`px-4 py-2 border-b-2 font-medium transition whitespace-nowrap ${
                      activeTab === 'pending' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Discursivas Pendentes ({filteredPendingAnswers.length})
                  </button>
                  <button 
                    onClick={() => setActiveTab('graded')}
                    className={`px-4 py-2 border-b-2 font-medium transition whitespace-nowrap ${
                      activeTab === 'graded' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Discursivas Corrigidas ({filteredGradedAnswers.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('tutoring')}
                    className={`px-4 py-2 border-b-2 font-medium transition whitespace-nowrap ${
                      activeTab === 'tutoring' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Dúvidas de Tutoria ({filteredTutoringTopics.length})
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-4 mb-4">
                {activeTab === 'tutoring' && (
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    Status
                    <select
                      value={tutoringStatusFilter}
                      onChange={(event) => setTutoringStatusFilter(event.target.value)}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Todas</option>
                      <option value="unanswered">Pendentes de resposta</option>
                      <option value="answered">Respondidas</option>
                    </select>
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  Ordenar por
                  <select
                    value={sortOrder}
                    onChange={(event) => setSortOrder(event.target.value)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="newest">Mais recentes</option>
                    <option value="oldest">Mais antigas</option>
                  </select>
                </label>
              </div>

              <div className="space-y-4">
                
                {activeTab === 'pending' && (
                  filteredPendingAnswers.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                      <p className="text-gray-600">Nenhuma resposta discursiva pendente para esta aula. Hora do café.</p>
                    </div>
                  ) : (
                    sortedPendingAnswers.map((answer) => (
                      <div key={answer.attempt_id} className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">Pendente</span>
                              <span className="text-sm text-gray-500">{new Date(answer.attempted_at).toLocaleString('pt-BR')}</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-1">
                              {answer.category_name} - {answer.chapter_title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">
                              Aluno: <span className="font-medium">{answer.username}</span> ({answer.email})
                            </p>
                          </div>
                        </div>

                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Questão:</h4>
                          <p className="text-gray-800 bg-gray-50 p-3 rounded border border-gray-200">{answer.question}</p>
                        </div>

                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Resposta do Aluno:</h4>
                          <p className="text-gray-800 bg-blue-50 p-4 rounded border border-blue-200 whitespace-pre-wrap">{answer.essay_answer}</p>
                        </div>

                        {editingId === answer.attempt_id ? (
                          <div className="border-t border-gray-200 pt-4 mt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Nota (0-100)</label>
                                <input
                                  type="number" min="0" max="100" value={grade}
                                  onChange={(e) => setGrade(e.target.value)}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="Ex: 85"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Feedback</label>
                                <textarea
                                  value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="Comentários..."
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => handleSubmitGrade(answer.attempt_id)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                                <FiSave /> Salvar Correção
                              </button>
                              <button onClick={handleCancelGrading} className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                                <FiX /> Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => handleStartGrading(answer)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                            <FiEdit /> Corrigir Resposta
                          </button>
                        )}
                      </div>
                    ))
                  )
                )}

                {activeTab === 'graded' && (
                  filteredGradedAnswers.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                      <p className="text-gray-600">Nenhuma resposta corrigida nesta aula. O vazio me assombra.</p>
                    </div>
                  ) : (
                    sortedGradedAnswers.map((answer) => (
                      <div key={answer.attempt_id} className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${answer.grade >= 60 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {answer.grade >= 60 ? 'Aprovado' : 'Reprovado'}
                              </span>
                              <span className="text-sm text-gray-500">Corrigido em: {new Date(answer.graded_at || answer.attempted_at).toLocaleString('pt-BR')}</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-1">{answer.category_name} - {answer.chapter_title}</h3>
                            <p className="text-sm text-gray-600 mb-2">Aluno: <span className="font-medium">{answer.username}</span> ({answer.email})</p>
                            <div className={`inline-block px-3 py-1 rounded text-sm font-semibold ${answer.grade >= 60 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                              Nota: {answer.grade}/100
                            </div>
                          </div>
                        </div>
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Questão:</h4>
                          <p className="text-gray-800 bg-gray-50 p-3 rounded border border-gray-200">{answer.question}</p>
                        </div>
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Resposta do Aluno:</h4>
                          <p className="text-gray-800 bg-blue-50 p-4 rounded border border-blue-200 whitespace-pre-wrap">{answer.essay_answer}</p>
                        </div>
                        {answer.feedback && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Feedback:</h4>
                            <p className={`p-4 rounded border whitespace-pre-wrap ${answer.grade >= 60 ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                              {answer.feedback}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  )
                )}

                {activeTab === 'tutoring' && (
                  <div className="space-y-6">
                    
                    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">Tutorias Gravadas</h3>
                          <p className="text-sm text-gray-500">Vídeos gravados por Aula, visíveis para alunos e gerenciados por admin/master.</p>
                        </div>
                        <button
                          onClick={() => {
                            handleCancelRecordedVideoEdit();
                          }}
                          className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                        >
                          Limpar formulário
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <label className="text-sm font-medium text-gray-700 md:col-span-1">
                          Título
                          <input
                            type="text"
                            value={recordedVideoForm.title}
                            onChange={(event) => setRecordedVideoForm((prev) => ({ ...prev, title: event.target.value }))}
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Ex: Revisão da Aula"
                          />
                        </label>
                        <label className="text-sm font-medium text-gray-700 md:col-span-1">
                          Link do vídeo
                          <input
                            type="url"
                            value={recordedVideoForm.videoUrl}
                            onChange={(event) => setRecordedVideoForm((prev) => ({ ...prev, videoUrl: event.target.value }))}
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="https://www.youtube.com/watch?v=..."
                          />
                        </label>
                        <label className="text-sm font-medium text-gray-700 md:col-span-1">
                          Ordem
                          <input
                            type="number"
                            min="1"
                            value={recordedVideoForm.orderIndex}
                            onChange={(event) => setRecordedVideoForm((prev) => ({ ...prev, orderIndex: event.target.value }))}
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="1"
                          />
                        </label>
                      </div>

                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={handleSubmitRecordedVideo}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                          {editingRecordedVideoId ? 'Salvar alteração' : 'Adicionar vídeo'}
                        </button>
                        {editingRecordedVideoId && (
                          <button
                            onClick={handleCancelRecordedVideoEdit}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                          >
                            Cancelar edição
                          </button>
                        )}
                      </div>

                      {recordedVideosError && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                          {recordedVideosError}
                        </div>
                      )}

                      {recordedVideosLoading ? (
                        <p className="text-sm text-gray-500">Carregando tutorias gravadas...</p>
                      ) : sortedRecordedVideos.length === 0 ? (
                        <p className="text-sm text-gray-500">Nenhuma tutoria gravada cadastrada.</p>
                      ) : (
                        <div className="space-y-3">
                          {sortedRecordedVideos.map((video) => (
                            <div key={video.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <p className="font-semibold text-gray-800">{video.title}</p>
                                  <p className="text-sm text-gray-600 break-all">{video.video_url}</p>
                                  <p className="text-xs text-gray-500 mt-1">Ordem {video.order_index}</p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                  <button
                                    onClick={() => handleStartRecordedVideoEdit(video)}
                                    className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRecordedVideo(video.id)}
                                    className="px-3 py-1 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                                  >
                                    Remover
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {tutoringError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {tutoringError}
                      </div>
                    )}
                    
                    {tutoringLoading ? (
                      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                        <p className="text-gray-600">Procurando dúvidas no banco de dados...</p>
                      </div>
                    ) : filteredTutoringTopics.length === 0 ? (
                      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                        <p className="text-gray-600">Nenhum tópico de tutoria para esta aula. Estão todos sabendo demais.</p>
                      </div>
                    ) : (
                      sortedTutoringTopics.map((topic) => (
                        <div key={topic.id} className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                {topic.is_faq === 1 && (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-700">FAQ</span>
                                )}
                                {topic.answer_text ? (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">Respondido</span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">Sem resposta</span>
                                )}
                                <span className="text-xs text-gray-500">{new Date(topic.created_at).toLocaleString('pt-BR')}</span>
                              </div>
                              <h5 className="text-lg font-semibold text-gray-800">{topic.title}</h5>
                              <p className="text-sm text-gray-600">Aluno: {topic.student_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {user?.role === 'admin_master' && (
                                <button onClick={() => handleToggleFaq(topic)} className="px-3 py-1 text-xs font-medium rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
                                  {topic.is_faq === 1 ? 'Remover FAQ' : 'Marcar FAQ'}
                                </button>
                              )}
                              <button onClick={() => handleToggleDetails(topic.id)} className="px-3 py-1 text-xs font-medium rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
                                {expandedTopicId === topic.id ? 'Ocultar comentários' : 'Ver comentários'}
                              </button>
                            </div>
                          </div>

                          <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap">
                            {topic.question}
                          </p>

                          {topic.answer_text && (
                            <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">{topic.answer_text}</p>
                              {topic.answered_by_name && (
                                <p className="text-xs text-gray-500 mt-2">Respondido por {topic.answered_by_name}</p>
                              )}
                            </div>
                          )}

                          {tutoringAnsweringId === topic.id ? (
                            <div className="mt-4">
                              <textarea
                                value={tutoringAnswerText} onChange={(e) => setTutoringAnswerText(e.target.value)} rows={3}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Digite a resposta oficial da doutrina..."
                              />
                              <div className="flex gap-2 mt-3">
                                <button onClick={() => handleSubmitAnswer(topic.id)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                                  <FiSave /> Salvar resposta
                                </button>
                                <button onClick={handleCancelAnswer} className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                                  <FiX /> Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => handleStartAnswer(topic)} className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                              <FiEdit /> {topic.answer_text ? 'Editar resposta' : 'Responder Dúvida'}
                            </button>
                          )}

                          {expandedTopicId === topic.id && (
                            <div className="mt-4 space-y-3">
                              <h6 className="text-sm font-semibold text-gray-700">Comentários Adicionais</h6>
                              {expandedTopicComments.length === 0 ? (
                                <p className="text-sm text-gray-500">Nenhum comentário registrado nesta discussão.</p>
                              ) : (
                                expandedTopicComments.map((comment) => (
                                  <div key={comment.id} className="bg-gray-50 border border-gray-200 rounded p-3">
                                    <div className="flex items-center justify-between">
                                      <p className="text-xs font-semibold text-gray-600">{comment.username}</p>
                                      <p className="text-xs text-gray-400">{new Date(comment.created_at).toLocaleString('pt-BR')}</p>
                                    </div>
                                    <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                                      {comment.content || 'Comentário removido pelo sistema'}
                                    </p>
                                    {user?.role === 'admin_master' && comment.is_removed !== 1 && (
                                      <button onClick={() => handleRemoveComment(comment.id)} className="mt-2 text-xs text-red-600 hover:text-red-700">
                                        Deletar comentário
                                      </button>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardAdmin;